import type { Point } from "../canvas/canvasTypes";
import type { Rect } from "../comic/comicTypes";
import {
  normalizeShapeGeometry,
  isUsableShapeGeometry,
  shapeTravelCssPixels,
  SHAPE_ACTIVATION_THRESHOLD_CSS_PIXELS,
} from "./shapeGeometry";
import type { ActiveShape, ShapeMode, ShapeStyle, ShapeTool } from "./shapeTypes";

interface ShapeInteractionStart {
  tool: ShapeTool;
  layerId: string;
  start: Point;
  zoom: number;
  style: ShapeStyle;
  clipRect: Rect;
  panelId?: string;
}

export class ShapeInteractionController {
  private start: ShapeInteractionStart | null = null;
  private preview: ActiveShape | null = null;

  public constructor(private readonly thresholdCssPixels = SHAPE_ACTIVATION_THRESHOLD_CSS_PIXELS) {}

  public begin(
    tool: ShapeTool,
    layerId: string,
    point: Readonly<Point>,
    zoom: number,
    style: Readonly<{ colour: string; size: number; opacity: number; mode: ShapeMode }>,
    clipRect: Readonly<Rect>,
    panelId?: string,
  ): void {
    this.start = {
      tool,
      layerId,
      start: { ...point },
      zoom: Math.max(0.05, zoom),
      style: { ...style },
      clipRect: { ...clipRect },
      ...(panelId ? { panelId } : {}),
    };
    this.preview = null;
  }

  public update(point: Readonly<Point>): boolean {
    const start = this.start;
    if (!start) return false;
    if (
      shapeTravelCssPixels(start.start, point, start.zoom) < this.thresholdCssPixels &&
      !this.preview
    ) {
      return false;
    }
    this.preview = Object.freeze({
      tool: start.tool,
      layerId: start.layerId,
      // Once activated, preserve the exact normalized pointer endpoints. The
      // only discontinuity is the intentional four-CSS-pixel dead zone.
      geometry: normalizeShapeGeometry(start.start, point, start.clipRect),
      style: Object.freeze({ ...start.style }),
      clipRect: Object.freeze({ ...start.clipRect }),
      ...(start.panelId ? { panelId: start.panelId } : {}),
    });
    return true;
  }

  public finish(): ActiveShape | null {
    const result =
      this.preview && isUsableShapeGeometry(this.preview.tool, this.preview.geometry)
        ? this.preview
        : null;
    this.start = null;
    this.preview = null;
    return result;
  }

  public cancel(): void {
    this.start = null;
    this.preview = null;
  }

  public isActive(): boolean {
    return this.start !== null;
  }

  public getPreview(): ActiveShape | null {
    return this.preview;
  }
}
